import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Callout, Card } from '@viniciomendesr/rayrovisk-design-system';
import { keys, useAuthorizeInstagram, useInstagramConnection } from '../api/queries';
import {
  CONNECT_BLOCKED,
  CONNECT_CONNECTED,
  CONNECT_DESCRIPTION,
  CONNECT_LABEL,
  CONNECT_POPUP_BLOCKED,
  CONNECT_TITLE
} from './mediaKitCopy';

/**
 * Authorizing Instagram to send more than what is public.
 *
 * Separate from `SocialLinksCard` and deliberately below it: that card is the
 * free path and says no password is ever asked for, which stays true here — the
 * authorization happens at Instagram and this app never sees a credential. What
 * changes is that Meta then answers for her account, so the two cards have to
 * read as two different offers rather than one contradicting the other.
 */
export function InstagramConnectCard() {
  const connection = useInstagramConnection();
  const authorize = useAuthorizeInstagram();
  const client = useQueryClient();
  const [popupBlocked, setPopupBlocked] = useState(false);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // The origin is checked before the payload is read: any window may post
      // here, and `data.type` is attacker-chosen.
      if (event.origin !== window.location.origin) {
        return;
      }
      if ((event.data as { type?: string } | null)?.type === 'instagram-oauth-complete') {
        void client.invalidateQueries({ queryKey: keys.instagramConnection });
        void client.invalidateQueries({ queryKey: keys.mediaKit });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [client]);

  // The route does not exist while the switch is off, and a card explaining a
  // capability nobody has is worse than no card.
  if (connection.isError || !connection.data) {
    return null;
  }

  async function begin() {
    // Opened before the await, and blank. Safari treats a popup opened after an
    // awaited call as unrequested and blocks it, which is what the check-in
    // flow in `web/` found first.
    const popup = window.open(
      'about:blank',
      'roteirovisk-instagram',
      'popup=yes,width=620,height=760'
    );
    if (!popup) {
      setPopupBlocked(true);
      return;
    }
    setPopupBlocked(false);
    try {
      const result = await authorize.mutateAsync();
      popup.location.replace(result.authorizationUrl);
    } catch {
      popup.close();
    }
  }

  const { connected, canConnect } = connection.data;

  return (
    <Card layer="functional" padding="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ font: '600 17px/1.3 var(--rvk-font-expressive)', margin: 0 }}>
              {CONNECT_TITLE}
            </h2>
            {connected && <Badge tone="success">Conectado</Badge>}
          </div>
          <p
            style={{
              font: '400 13px/1.5 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              margin: 0
            }}
          >
            {connected ? CONNECT_CONNECTED : CONNECT_DESCRIPTION}
          </p>
        </div>

        {popupBlocked && <Callout tone="warning">{CONNECT_POPUP_BLOCKED}</Callout>}
        {authorize.isError && (
          <Callout tone="warning" live="status">
            {(authorize.error as Error).message}
          </Callout>
        )}

        {!connected && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            {/* Written out, not hung on the button. `accessReason` only reaches
                a `title` for `permission-required`, so an `unavailable` button
                is a grey rectangle with no explanation anywhere near it. */}
            {!canConnect && (
              <span
                style={{
                  font: '400 13px/1.5 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-tertiary)'
                }}
              >
                {CONNECT_BLOCKED}
              </span>
            )}
            <Button
              loading={authorize.isPending}
              onClick={() => void begin()}
              access={canConnect ? undefined : 'unavailable'}
              accessReason={CONNECT_BLOCKED}
            >
              {CONNECT_LABEL}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
